import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useMessaging } from '../context/MessagingContext';

export default function MessagingPanel({ open, onClose }) {
  const {
    connected, channels, activeChannelId, messages, typingUsers,
    openChannel, sendMessage, editMessage, deleteMessage, notifyTyping,
    fetchChannels, fetchMessages,
  } = useMessaging();

  const [draft, setDraft] = useState('');
  const [sendError, setSendError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [newChannelName, setNewChannelName] = useState('');
  const [showNewChannel, setShowNewChannel] = useState(false);
  const [creatingChannel, setCreatingChannel] = useState(false);
  const [channelError, setChannelError] = useState('');
  const [showMembers, setShowMembers] = useState(false);
  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [deletingChannelId, setDeletingChannelId] = useState(null); // id pending confirm
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // ── Mobile single-pane layout ───────────────────────────────────────────────
  // Below this width, show one pane at a time (list, thread, or members)
  // instead of squeezing all of them side by side.
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 640);
  const [mobileView, setMobileView] = useState('list'); // 'list' | 'thread' | 'members'

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const activeMessages = activeChannelId ? (messages[activeChannelId] || []) : [];
  const activeChannel = channels.find(c => String(c.id) === String(activeChannelId));
  const typing = activeChannelId ? (typingUsers[activeChannelId] || []) : [];

  // Reload channels when panel opens, and re-pull the active thread too — otherwise
  // a connection gap only ever surfaces itself once you switch channels.
  useEffect(() => {
    if (!open) return;
    fetchChannels();
    if (activeChannelId) fetchMessages(activeChannelId);
  }, [open, fetchChannels, activeChannelId, fetchMessages]);

  // Clear any stale send error when switching channels
  useEffect(() => { setSendError(''); }, [activeChannelId]);

  // Close members pane when switching channels
  useEffect(() => {
    setShowMembers(false);
  }, [activeChannelId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (open && activeChannelId) {
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  }, [activeMessages.length, open, activeChannelId]);

  // Focus input when channel selected
  useEffect(() => {
    if (activeChannelId) inputRef.current?.focus();
  }, [activeChannelId]);

  const handleSend = useCallback(async () => {
    if (!draft.trim() || !activeChannelId) return;
    const body = draft.trim();
    setDraft('');
    setSendError('');
    const result = await sendMessage(activeChannelId, body);
    if (!result?.ok) {
      setSendError(result?.error || 'Failed to send message.');
      setDraft(body); // don't lose what they typed
    }
  }, [draft, activeChannelId, sendMessage]);

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const startEdit = (msg) => {
    setEditingId(msg.id);
    setEditBody(msg.body);
  };

  const submitEdit = () => {
    if (editBody.trim()) editMessage(editingId, editBody.trim());
    setEditingId(null);
    setEditBody('');
  };

  const cancelEdit = () => { setEditingId(null); setEditBody(''); };

  const { meId, isAdmin } = (() => {
    try {
      const p = JSON.parse(atob(localStorage.getItem('token').split('.')[1]));
      return { meId: p.id, isAdmin: p.role === 'admin' || p.role === 'superadmin' };
    } catch { return { meId: null, isAdmin: false }; }
  })();

  const createChannel = useCallback(async () => {
    if (!newChannelName.trim() || creatingChannel) return;
    setCreatingChannel(true);
    setChannelError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/messaging/channels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: newChannelName.trim() }),
      });
      if (res.ok) {
        setNewChannelName('');
        setShowNewChannel(false);
        setChannelError('');
        fetchChannels();
      } else {
        const data = await res.json().catch(() => ({}));
        setChannelError(res.status === 409 ? 'A channel with that name already exists.' : (data.error || 'Failed to create channel.'));
      }
    } catch {
      setChannelError('Could not reach the server.');
    } finally {
      setCreatingChannel(false);
    }
  }, [newChannelName, creatingChannel, fetchChannels]);

  const API = process.env.REACT_APP_API_URL || '';
  const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const openMembers = useCallback(async (channelId) => {
    setLoadingMembers(true);
    try {
      const [mRes, uRes] = await Promise.all([
        fetch(`${API}/api/messaging/channels/${channelId}/members`, { headers: authH() }),
        fetch(`${API}/api/messaging/users`, { headers: authH() }),
      ]);
      setMembers(mRes.ok ? await mRes.json() : []);
      setAllUsers(uRes.ok ? await uRes.json() : []);
    } catch { /* ignore */ } finally {
      setLoadingMembers(false);
    }
  }, [API]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMembers = useCallback(() => {
    const next = !showMembers;
    setShowMembers(next);
    setMobileView(next ? 'members' : 'thread');
    if (next && activeChannelId) openMembers(activeChannelId);
  }, [showMembers, activeChannelId, openMembers]);

  const selectChannel = useCallback((channelId) => {
    openChannel(channelId);
    setMobileView('thread');
  }, [openChannel]);

  const backToList = useCallback(() => {
    setShowMembers(false);
    setMobileView('list');
  }, []);

  const addMember = useCallback(async (userId) => {
    if (!activeChannelId) return;
    await fetch(`${API}/api/messaging/channels/${activeChannelId}/members`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authH() },
      body: JSON.stringify({ userId }),
    });
    openMembers(activeChannelId);
  }, [activeChannelId, API, openMembers]); // eslint-disable-line react-hooks/exhaustive-deps

  const removeMember = useCallback(async (userId) => {
    if (!activeChannelId) return;
    await fetch(`${API}/api/messaging/channels/${activeChannelId}/members/${userId}`, {
      method: 'DELETE', headers: authH(),
    });
    openMembers(activeChannelId);
  }, [activeChannelId, API, openMembers]); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteChannel = useCallback(async (channelId) => {
    await fetch(`${API}/api/messaging/channels/${channelId}`, { method: 'DELETE', headers: authH() });
    setDeletingChannelId(null);
    fetchChannels();
  }, [API, fetchChannels]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmtTime = (ts) => {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const fmtDate = (ts) => {
    const d = new Date(ts);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return 'Today';
    const yest = new Date(today); yest.setDate(yest.getDate() - 1);
    if (d.toDateString() === yest.toDateString()) return 'Yesterday';
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Group messages by date
  const grouped = (() => {
    const groups = [];
    let lastDate = null;
    activeMessages.forEach(m => {
      const d = fmtDate(m.createdAt);
      if (d !== lastDate) { groups.push({ type: 'date', label: d }); lastDate = d; }
      groups.push({ type: 'message', msg: m });
    });
    return groups;
  })();

  return (
    <>
      {/* Backdrop — fades in/out */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)',
          zIndex: 1049,
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 0.25s ease',
        }}
      />

      {/* Panel — slides in/out from right */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 680, maxWidth: '95vw',
        background: 'var(--bs-body-bg, #fff)',
        display: 'flex', flexDirection: 'column',
        zIndex: 1050, boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
        overflow: 'hidden',
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        pointerEvents: open ? 'auto' : 'none',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '14px 18px', borderBottom: '1px solid var(--bs-border-color, #dee2e6)',
          flexShrink: 0,
        }}>
          {isMobile && mobileView !== 'list' && (
            <button
              onClick={backToList}
              style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 20, padding: '0 2px 0 0', lineHeight: 1, flexShrink: 0 }}
              aria-label="Back to channels"
            >←</button>
          )}
          <span style={{ fontSize: 18, fontWeight: 600 }}>
            {activeChannel ? `# ${activeChannel.name}` : 'Messages'}
          </span>
          {!connected && (
            <span
              style={{ fontSize: 11, color: '#dc3545', display: 'flex', alignItems: 'center', gap: 4 }}
              title="Not connected — reconnecting…"
            >
              ● Reconnecting…
            </span>
          )}
          {activeChannel?.description && (
            <span style={{ fontSize: 13, color: 'var(--bs-secondary, #6c757d)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              — {activeChannel.description}
            </span>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
            {activeChannel && isAdmin && (
              <button
                className={`btn btn-sm ${showMembers ? 'btn-secondary' : 'btn-outline-secondary'}`}
                style={{ fontSize: 12 }}
                onClick={toggleMembers}
              >
                👥 Members
              </button>
            )}
            <button className="btn-close" onClick={onClose} aria-label="Close" />
          </div>
        </div>

        {/* Body */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Sidebar — on mobile, only shown while browsing the channel list */}
          {(!isMobile || mobileView === 'list') && (
          <div style={{
            width: isMobile ? '100%' : 180, flexShrink: 0,
            borderRight: isMobile ? 'none' : '1px solid var(--bs-border-color, #dee2e6)',
            overflowY: 'auto', padding: '8px 0',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 8px 6px 12px' }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Channels</span>
              <button
                onClick={() => setShowNewChannel(v => !v)}
                style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 16, color: '#6c757d', lineHeight: 1, padding: '0 2px' }}
                title="New channel"
              >+</button>
            </div>
            {showNewChannel && (
              <div style={{ padding: '4px 8px 8px' }}>
                <input
                  className={`form-control form-control-sm${channelError ? ' is-invalid' : ''}`}
                  placeholder="channel-name"
                  value={newChannelName}
                  onChange={e => { setChannelError(''); setNewChannelName(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')); }}
                  onKeyDown={e => { if (e.key === 'Enter') createChannel(); if (e.key === 'Escape') { setShowNewChannel(false); setNewChannelName(''); setChannelError(''); } }}
                  autoFocus
                  maxLength={40}
                />
                {channelError && <div style={{ fontSize: 11, color: '#dc3545', marginTop: 3 }}>{channelError}</div>}
                <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                  <button className="btn btn-sm btn-primary" style={{ fontSize: 11, flex: 1 }} onClick={createChannel} disabled={!newChannelName.trim() || creatingChannel}>
                    {creatingChannel ? '…' : 'Create'}
                  </button>
                  <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: 11 }} onClick={() => { setShowNewChannel(false); setNewChannelName(''); setChannelError(''); }}>✕</button>
                </div>
              </div>
            )}
            {channels.map(ch => (
              <div key={ch.id} style={{ display: 'flex', alignItems: 'center', margin: '1px 4px', borderRadius: 4, maxWidth: 'calc(100% - 8px)' }} className="channel-row">
                <button
                  onClick={() => selectChannel(ch.id)}
                  style={{
                    flex: 1, textAlign: 'left', border: 'none', minWidth: 0,
                    padding: '6px 8px 6px 12px', cursor: 'pointer', fontSize: 14,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    borderRadius: 4,
                    background: String(ch.id) === String(activeChannelId) ? 'rgba(13,110,253,0.1)' : 'transparent',
                    color: String(ch.id) === String(activeChannelId) ? '#0d6efd' : 'inherit',
                    fontWeight: String(ch.id) === String(activeChannelId) ? 600 : 400,
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    # {ch.name}
                  </span>
                  {ch.unreadCount > 0 && String(ch.id) !== String(activeChannelId) && (
                    <span className="badge bg-danger rounded-pill" style={{ fontSize: 10, flexShrink: 0, marginLeft: 4 }}>
                      {ch.unreadCount > 99 ? '99+' : ch.unreadCount}
                    </span>
                  )}
                </button>
                {isAdmin && deletingChannelId === ch.id ? (
                  <div style={{ display: 'flex', gap: 2, paddingRight: 4, flexShrink: 0 }}>
                    <button
                      className="btn btn-danger"
                      style={{ padding: '1px 6px', fontSize: 11, lineHeight: 1.4 }}
                      onClick={() => deleteChannel(ch.id)}
                    >Yes</button>
                    <button
                      className="btn btn-outline-secondary"
                      style={{ padding: '1px 6px', fontSize: 11, lineHeight: 1.4 }}
                      onClick={() => setDeletingChannelId(null)}
                    >No</button>
                  </div>
                ) : isAdmin ? (
                  <button
                    className="channel-delete-btn"
                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#dc3545', fontSize: 13, padding: '0 6px', flexShrink: 0 }}
                    onClick={e => { e.stopPropagation(); setDeletingChannelId(ch.id); }}
                    title="Delete channel"
                  >🗑</button>
                ) : null}
              </div>
            ))}
          </div>
          )}

          {/* Thread — on mobile, only shown once a channel is picked */}
          {(!isMobile || mobileView === 'thread') && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
            {!activeChannelId ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6c757d' }}>
                Select a channel to start chatting
              </div>
            ) : (
              <>
                {/* Messages */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
                  {grouped.map((item, i) => {
                    if (item.type === 'date') {
                      return (
                        <div key={`date-${i}`} style={{ textAlign: 'center', margin: '12px 0 8px', fontSize: 12, color: '#6c757d' }}>
                          <span style={{ background: 'var(--bs-border-color,#dee2e6)', padding: '2px 10px', borderRadius: 10 }}>
                            {item.label}
                          </span>
                        </div>
                      );
                    }
                    const { msg } = item;
                    const isMe = msg.authorUserId && String(msg.authorUserId) === String(meId);
                    const isSystem = msg.type === 'system';
                    if (isSystem) {
                      return (
                        <div key={msg.id} style={{ textAlign: 'center', fontSize: 12, color: '#6c757d', margin: '6px 0', fontStyle: 'italic' }}>
                          {msg.body}
                        </div>
                      );
                    }
                    return (
                      <div
                        key={msg.id}
                        style={{ display: 'flex', gap: 10, marginBottom: 8, alignItems: 'flex-start' }}
                        className="messaging-msg"
                      >
                        {/* Avatar */}
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                          background: isMe ? '#0d6efd' : '#6c757d',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff', fontSize: 13, fontWeight: 600,
                        }}>
                          {(msg.authorName || '?')[0].toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 2 }}>
                            <span style={{ fontWeight: 600, fontSize: 14 }}>{msg.authorName}</span>
                            <span style={{ fontSize: 11, color: '#6c757d' }}>{fmtTime(msg.createdAt)}</span>
                            {msg.editedAt && <span style={{ fontSize: 11, color: '#6c757d' }}>(edited)</span>}
                          </div>
                          {editingId === msg.id ? (
                            <div style={{ display: 'flex', gap: 6 }}>
                              <input
                                className="form-control form-control-sm"
                                value={editBody}
                                onChange={e => setEditBody(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') submitEdit(); if (e.key === 'Escape') cancelEdit(); }}
                                autoFocus
                              />
                              <button className="btn btn-sm btn-primary" onClick={submitEdit}>Save</button>
                              <button className="btn btn-sm btn-outline-secondary" onClick={cancelEdit}>Cancel</button>
                            </div>
                          ) : (
                            <div style={{ fontSize: 14, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                              {msg.body}
                            </div>
                          )}
                        </div>
                        {isMe && !editingId && (
                          <div className="msg-actions" style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                            <button
                              className="btn btn-sm btn-link p-0"
                              style={{ fontSize: 12, color: '#6c757d' }}
                              onClick={() => startEdit(msg)}
                              title="Edit"
                            >
                              ✏️
                            </button>
                            <button
                              className="btn btn-sm btn-link p-0"
                              style={{ fontSize: 12, color: '#dc3545' }}
                              onClick={() => deleteMessage(msg.id)}
                              title="Delete"
                            >
                              🗑️
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {typing.length > 0 && (
                    <div style={{ fontSize: 12, color: '#6c757d', fontStyle: 'italic', marginTop: 4 }}>
                      {typing.join(', ')} {typing.length === 1 ? 'is' : 'are'} typing…
                    </div>
                  )}
                  <div ref={bottomRef} />
                </div>

                {/* Input */}
                <div style={{
                  padding: '12px 16px', borderTop: '1px solid var(--bs-border-color,#dee2e6)', flexShrink: 0,
                }}>
                  {sendError && (
                    <div style={{ fontSize: 12, color: '#dc3545', marginBottom: 6 }}>{sendError}</div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      ref={inputRef}
                      className="form-control"
                      placeholder={`Message #${activeChannel?.name || ''}`}
                      value={draft}
                      onChange={e => { setDraft(e.target.value); notifyTyping(activeChannelId); }}
                      onKeyDown={handleKey}
                      maxLength={2000}
                      style={{ fontSize: 16 }}
                    />
                    <button
                      className="btn btn-primary"
                      onClick={handleSend}
                      disabled={!draft.trim()}
                      style={{ flexShrink: 0 }}
                    >
                      Send
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
          )}

          {/* Members pane — 3rd flex column inside the body (full-width takeover on mobile) */}
          {showMembers && activeChannel && isAdmin && (!isMobile || mobileView === 'members') && (
            <div style={{
              width: isMobile ? '100%' : 220, flexShrink: 0,
              borderLeft: isMobile ? 'none' : '1px solid var(--bs-border-color, #dee2e6)',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}>
              <div style={{
                padding: '14px 14px 10px', fontWeight: 600, fontSize: 13,
                borderBottom: '1px solid var(--bs-border-color, #dee2e6)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                flexShrink: 0,
              }}>
                Members
                <button
                  style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, color: '#6c757d', lineHeight: 1 }}
                  onClick={() => { setShowMembers(false); setMobileView('thread'); }}
                >✕</button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
                {loadingMembers ? (
                  <div style={{ textAlign: 'center', padding: 20, color: '#999', fontSize: 13 }}>Loading…</div>
                ) : (
                  <>
                    {members.length === 0 && (
                      <div style={{ padding: '8px 14px', color: '#999', fontSize: 12 }}>No members yet.</div>
                    )}
                    {members.map(m => (
                      <div key={m.userId} style={{ display: 'flex', alignItems: 'center', padding: '5px 12px', gap: 8 }}>
                        <div style={{
                          width: 26, height: 26, borderRadius: '50%', background: '#6c757d',
                          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700, flexShrink: 0,
                        }}>
                          {(m.name || '?')[0].toUpperCase()}
                        </div>
                        <span style={{ fontSize: 13, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</span>
                        <button
                          style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#dc3545', fontSize: 12, flexShrink: 0 }}
                          onClick={() => removeMember(m.userId)}
                          title="Remove"
                        >✕</button>
                      </div>
                    ))}
                    <div style={{
                      borderTop: '1px solid var(--bs-border-color, #dee2e6)', marginTop: 8,
                      padding: '8px 12px 4px', fontSize: 10, fontWeight: 600,
                      color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.05em',
                    }}>
                      Add people
                    </div>
                    {allUsers.filter(u => !members.find(m => m.userId === u.id)).map(u => (
                      <button
                        key={u.id}
                        onClick={() => addMember(u.id)}
                        style={{
                          width: '100%', textAlign: 'left', border: 'none', background: 'none',
                          padding: '5px 12px', cursor: 'pointer', fontSize: 13,
                          display: 'flex', alignItems: 'center', gap: 8,
                        }}
                      >
                        <div style={{
                          width: 26, height: 26, borderRadius: '50%', background: '#0d6efd',
                          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700, flexShrink: 0,
                        }}>
                          {(u.name || '?')[0].toUpperCase()}
                        </div>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{u.name}</span>
                        <span style={{ fontSize: 12, color: '#0d6efd', flexShrink: 0 }}>+</span>
                      </button>
                    ))}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .channel-delete-btn { opacity: 0; }
        /* Hover-reveal only on devices that truly support hover (mouse/trackpad).
           On touch devices, :hover rules make the FIRST tap simulate hover instead
           of firing the click — the button/row needs a second tap to register.
           Keeping actions always visible on touch avoids that double-tap trap. */
        @media (hover: hover) {
          .messaging-msg .msg-actions { opacity: 0; transition: opacity 0.15s; }
          .messaging-msg:hover .msg-actions { opacity: 1; }
          .channel-row:hover .channel-delete-btn { opacity: 1; }
        }
        @media (hover: none) {
          .messaging-msg .msg-actions { opacity: 1; }
          .channel-delete-btn { opacity: 0.6; }
        }
      `}</style>
    </>
  );
}
